import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  AudioLines,
  ChevronDown,
  Film,
  Image,
  LoaderCircle,
  MessageCircle,
  Music2,
  RefreshCw,
} from "lucide-react";
import { api, post } from "./api";
import { Button, Mark, relativeTime } from "./components";
import {
  CATALOG_TEXT,
  creditNumber,
  MEDIA_CATALOG,
  INFERENCE_CATALOG,
  PLANS,
  TOPUP,
  type MediaKind,
} from "../shared/catalog";
import { exVat } from "./Plans";
import { SenalOffer, useSenal } from "./Senal";
import type {
  Account,
  CreditCategory,
  StudioConfig,
  Subscription,
  VideoSpend,
  Wallet,
} from "../shared/types";
import { STAGES } from "../shared/progress";
import { bilingual, lang, locale, text, useText } from "./i18n";

const TEXT = bilingual({
  en: {
    categories: {
      inference: "Direction",
      speech: "Voice",
      image: "Images",
      music: "Music",
      video: "AI footage",
      transcription: "Transcription",
    } satisfies Record<CreditCategory, string>,
    balances: {
      plan: "This month",
      topup: "Extra",
      gift: "Señal credits",
      welcome: "Welcome",
      other: "Granted",
      lastMonth: "Last month",
      until: (day: string) => `until ${day}`,
      renew: (day: string) => `renew ${day}`,
      noEnd: "no end date",
    },
    plan: {
      preview: "Local preview credits",
      none: "No plan yet.",
      startsAt: (price: string) => `Plans start at ${price} a month.`,
      seePlans: "See plans",
      yours: "Your plan",
      arranged: "Arranged with the Foco team",
      ends: (day: string) => `Ends ${day}`,
      monthly: (credits: string, day: string, next?: string) =>
        `${credits} a month · renews ${day}${next ? ` as ${next}` : ""}`,
      invoices: "Invoices and payment",
      change: "Change plan",
      add: (credits: string, price: string) => `Add ${credits} · ${price}`,
    },
    wallet: {
      title: "Credits",
      refreshLabel: "Refresh credit balance",
      refresh: "Refresh",
      balance: "Credit balance",
      previewBalance: "Preview balance",
      available: "Available",
      credits: "credits",
      meter: "Credits available, reserved and used",
      reserved: "Reserved",
      used: "Used",
      lasts: "When your credits end",
      spend: "Where it goes",
      rates: "Rates",
      direction: (model: string) => `${model} direction`,
      inOut: (input: string, output: string) => `${input} in · ${output} out`,
      perTokens: "per 1,000 tokens",
      optIn: "Opt-in",
      ownClaude: "Your Claude uses your own Claude account for inference. ",
      free: "Editing and rendering are free.",
    },
    videos: {
      title: "By video",
      average: (credits: string) => `${credits} per video on average`,
      none: "Nothing spent yet.",
      deleted: "Deleted",
      requests: (n: number) => `${n} ${n === 1 ? "request" : "requests"}`,
      open: "Open video",
    },
    request: {
      director: "Director's work",
      generated: "Generated",
      media: {
        inference: ["Direction", "Direction"],
        speech: ["Voice recording", "Voice recordings"],
        image: ["Image", "Images"],
        music: ["Original song", "Original songs"],
        video: ["AI footage clip", "AI footage clips"],
        transcription: ["Transcription", "Transcriptions"],
      } satisfies Record<CreditCategory, [string, string]>,
      yourClaude: "Your Claude",
      underMinute: "under a minute",
      worked: (time: string) => ` · worked ${time}`,
      onHold: (credits: string) => ` · ${credits} on hold`,
      made: " · made ",
    },
  },
  es: {
    categories: {
      inference: "Dirección",
      speech: "Voz",
      image: "Imágenes",
      music: "Música",
      video: "Clips con IA",
      transcription: "Transcripción",
    },
    balances: {
      plan: "Este mes",
      topup: "Extra",
      gift: "Créditos de Señal",
      welcome: "Bienvenida",
      other: "Concedidos",
      lastMonth: "Mes anterior",
      until: (day: string) => `hasta el ${day}`,
      renew: (day: string) => `se renuevan el ${day}`,
      noEnd: "sin fecha de fin",
    },
    plan: {
      preview: "Créditos de la vista previa local",
      none: "Aún no tienes plan.",
      startsAt: (price: string) => `Planes desde ${price} al mes.`,
      seePlans: "Ver planes",
      yours: "Tu plan",
      arranged: "Acordado con el equipo de Foco",
      ends: (day: string) => `Termina el ${day}`,
      monthly: (credits: string, day: string, next?: string) =>
        next
          ? `${credits} al mes · pasa a ${next} el ${day}`
          : `${credits} al mes · se renueva el ${day}`,
      invoices: "Facturas y pago",
      change: "Cambiar plan",
      add: (credits: string, price: string) => `Añadir ${credits} · ${price}`,
    },
    wallet: {
      title: "Créditos",
      refreshLabel: "Actualizar saldo de créditos",
      refresh: "Actualizar",
      balance: "Saldo de créditos",
      previewBalance: "Saldo de vista previa",
      available: "Disponibles",
      credits: "créditos",
      meter: "Créditos disponibles, reservados y usados",
      reserved: "Reservados",
      used: "Usados",
      lasts: "Cuándo caducan tus créditos",
      spend: "En qué se gastan",
      rates: "Tarifas",
      direction: (model: string) => `Dirección con ${model}`,
      inOut: (input: string, output: string) =>
        `${input} entrada · ${output} salida`,
      perTokens: "por 1000 tokens",
      optIn: "Opcional",
      ownClaude:
        "Tu Claude usa tu propia cuenta de Claude para la inferencia. ",
      free: "La edición y el renderizado son gratis.",
    },
    videos: {
      title: "Por vídeo",
      average: (credits: string) => `${credits} por vídeo de media`,
      none: "Aún no has gastado nada.",
      deleted: "Eliminado",
      requests: (n: number) => (n === 1 ? "1 petición" : `${n} peticiones`),
      open: "Abrir vídeo",
    },
    request: {
      director: "Trabajo del director",
      generated: "Generado",
      media: {
        inference: ["Dirección", "Dirección"],
        speech: ["Locución", "Locuciones"],
        image: ["Imagen", "Imágenes"],
        music: ["Canción original", "Canciones originales"],
        video: ["Clip con IA", "Clips con IA"],
        transcription: ["Transcripción", "Transcripciones"],
      },
      yourClaude: "Tu Claude",
      underMinute: "menos de un minuto",
      worked: (time: string) => ` · ${time} de trabajo`,
      onHold: (credits: string) => ` · ${credits} reservados`,
      made: " · creó ",
    },
  },
});
const credits = (n: number) => creditNumber(n, lang());
const icons = {
  inference: MessageCircle,
  speech: AudioLines,
  image: Image,
  music: Music2,
  video: Film,
  transcription: AudioLines,
};
export function useWallet() {
  const [wallet, setWallet] = useState<Wallet | null>(null),
    [error, setError] = useState("");
  const refresh = useCallback(() => {
    void api<Wallet>("/credits")
      .then((w) => {
        setWallet(w);
        setError("");
      })
      .catch((e) => setError(e.message));
  }, []);
  useEffect(() => {
    refresh();
    const timer = setInterval(refresh, 15000);
    window.addEventListener("focus", refresh);
    window.addEventListener("cutline:credits", refresh);
    return () => {
      clearInterval(timer);
      window.removeEventListener("focus", refresh);
      window.removeEventListener("cutline:credits", refresh);
    };
  }, [refresh]);
  return { wallet, error, refresh };
}

const day = (iso: string, year = false) =>
  new Date(iso).toLocaleDateString(locale(), {
    day: "numeric",
    month: "short",
    ...(year ? { year: "numeric" } : {}),
  });
const moment = (iso: string) =>
  new Date(iso).toLocaleString(locale(), {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });

/** A balance's name and when it ends. Last month's plan credits stay usable for a few hours after a renewal. */
function lasts(
  b: NonNullable<Wallet["balances"]>[number],
  sub: Subscription | null,
): [string, string] {
  const t = text(TEXT).balances;
  if (b.kind !== "plan")
    return [t[b.kind], b.expires ? t.until(day(b.expires, true)) : t.noEnd];
  if (sub && (!b.expires || Date.parse(b.expires) >= Date.parse(sub.renews)))
    return [
      t.plan,
      sub.cancelling ? t.until(day(sub.renews)) : t.renew(day(sub.renews)),
    ];
  return [t.lastMonth, b.expires ? t.until(moment(b.expires)) : t.noEnd];
}

/** The account's plan, with the ways to get more out of it. */
function PlanBar({
  account,
  config,
  onError,
}: {
  account: Account;
  config: StudioConfig;
  onError: (s: string) => void;
}) {
  const t = useText(TEXT).plan;
  const [busy, setBusy] = useState("");
  const sub = account.subscription;
  const open = (path: string) => {
    setBusy(path);
    void post(path)
      .then(({ url }) => window.location.assign(url))
      .catch((e) => {
        onError(e.message);
        setBusy("");
      });
  };
  if (config.preview)
    return (
      <div className="grant">
        <span>{t.preview}</span>
      </div>
    );
  if (account.plan !== "active")
    return (
      <div className="grant">
        <span>
          <b>{t.none}</b> {t.startsAt(exVat(PLANS.starter.price))}
        </span>
        <Link className="button primary" to="/plans">
          {t.seePlans} <ArrowRight size={15} />
        </Link>
      </div>
    );
  return (
    <div className="grant plan-bar">
      <span className="pb-plan">
        <b>{sub ? `${PLANS[sub.tier].name}` : t.yours}</b>
        <small>
          {!sub
            ? t.arranged
            : sub.cancelling
              ? t.ends(day(sub.renews))
              : t.monthly(
                  credits(PLANS[sub.tier].credits),
                  day(sub.renews),
                  sub.next ? PLANS[sub.next].name : undefined,
                )}
        </small>
      </span>
      <span className="pb-actions">
        {sub && (
          <>
            <button
              className="text-button"
              disabled={!!busy}
              onClick={() => open("/billing/portal")}
            >
              {busy === "/billing/portal" && (
                <LoaderCircle size={14} className="spin" />
              )}
              {t.invoices}
            </button>
            <Link className="button ghost" to="/plans">
              {t.change}
            </Link>
          </>
        )}
        <Button
          className="primary"
          disabled={!config.billing || !!busy}
          onClick={() => open("/billing/topup")}
        >
          {busy === "/billing/topup" && (
            <LoaderCircle size={15} className="spin" />
          )}
          {t.add(credits(TOPUP.credits), exVat(TOPUP.price))}
        </Button>
      </span>
    </div>
  );
}

export function CreditsScreen({
  account,
  config,
  onError,
}: {
  account: Account;
  config: StudioConfig;
  onError: (s: string) => void;
}) {
  const copy = useText(TEXT),
    t = copy.wallet,
    media = useText(CATALOG_TEXT).media;
  const { wallet, error, refresh } = useWallet();
  const senal = useSenal(account, config) && !!account.subscription;
  const available = wallet?.available || 0,
    held = wallet?.reserved || 0,
    spent = wallet?.spent || 0,
    total = Math.max(available + held + spent, 1);
  const spentBy = new Map(
    (wallet?.categories || []).map((c) => [c.category, c.amount]),
  );
  const categories = (
    ["inference", "speech", "image", "music", "video"] as CreditCategory[]
  ).map((category) => ({ category, amount: spentBy.get(category) || 0 }));
  const topMax = Math.max(1, ...categories.map((c) => c.amount));
  return (
    <div className="page credits page-enter">
      <header className="page-head">
        <h1 className="h1">{t.title}</h1>
        <button
          className="icon-button"
          onClick={refresh}
          aria-label={t.refreshLabel}
          title={t.refresh}
        >
          <RefreshCw size={16} />
        </button>
      </header>
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      <div className="cr-grid">
        <section className="card balance" aria-label={t.balance}>
          <span className="lbl">
            {config.preview ? t.previewBalance : t.available}
          </span>
          <div className="balance-n">
            <b className="tnum">{wallet ? credits(available) : "—"}</b>
            <span>{t.credits}</span>
          </div>
          <div
            className="meter"
            role="meter"
            aria-label={t.meter}
            aria-valuemin={0}
            aria-valuemax={total}
            aria-valuenow={available}
          >
            <i className="a" style={{ flexGrow: available / total }} />
            {held > 0 && <i className="r" style={{ flexGrow: held / total }} />}
            {spent > 0 && (
              <i className="s" style={{ flexGrow: spent / total }} />
            )}
          </div>
          <dl className="legend">
            <div>
              <dt>
                <i className="a" /> {t.available}
              </dt>
              <dd className="tnum">{credits(available)}</dd>
            </div>
            <div>
              <dt>
                <i className="r" /> {t.reserved}
              </dt>
              <dd className="tnum">{credits(held)}</dd>
            </div>
            <div>
              <dt>
                <i className="s" /> {t.used}
              </dt>
              <dd className="tnum">{credits(spent)}</dd>
            </div>
          </dl>
          {!config.preview && !!wallet?.balances?.length && (
            <ul className="lasts" aria-label={t.lasts}>
              {wallet.balances.map((b) => {
                const [name, ends] = lasts(b, account.subscription);
                return (
                  <li key={`${b.kind}${b.expires}`}>
                    <span>{name}</span>
                    <b className="tnum">{credits(b.left)}</b>
                    <small>{ends}</small>
                  </li>
                );
              })}
            </ul>
          )}
          <PlanBar account={account} config={config} onError={onError} />
          {senal && <SenalOffer site={config.senal} />}
        </section>
        <section className="card spend" aria-labelledby="spend-title">
          <h2 className="h2" id="spend-title">
            {t.spend}
          </h2>
          <ul className="bars">
            {categories.map((c) => {
              const Icon = icons[c.category];
              return (
                <li key={c.category}>
                  <span className="bar-l">
                    <Icon size={15} /> {copy.categories[c.category]}
                  </span>
                  <span className="bar">
                    <i style={{ width: `${(c.amount / topMax) * 100}%` }} />
                  </span>
                  <span className="mono">{credits(c.amount)}</span>
                </li>
              );
            })}
          </ul>
        </section>
        <ByVideo />
        <section className="card rates" aria-labelledby="rates-title">
          <h2 className="h2" id="rates-title">
            {t.rates}
          </h2>
          <ul className="rate-list">
            {Object.values(INFERENCE_CATALOG.models).map((m) => (
              <li key={m.name}>
                <MessageCircle size={15} />
                <span>{t.direction(m.name)}</span>
                <span className="mono">
                  {t.inOut(
                    credits(m.inputCreditsPerMillion / 1000),
                    credits(m.outputCreditsPerMillion / 1000),
                  )}
                </span>
                <small>{t.perTokens}</small>
              </li>
            ))}
            {Object.entries(MEDIA_CATALOG).map(([kind, c]) => {
              const Icon = icons[kind as CreditCategory],
                shown = media[kind as MediaKind];
              return (
                <li key={kind}>
                  <Icon size={15} />
                  <span>
                    {shown.label}
                    {kind === "video" && <em className="opt">{t.optIn}</em>}
                  </span>
                  <span className="mono">{credits(c.credits)}</span>
                  <small>{shown.unit}</small>
                </li>
              );
            })}
          </ul>
          <p className="muted small">
            {config.nativeClaude && t.ownClaude}
            {t.free}
          </p>
        </section>
      </div>
    </div>
  );
}

const lasted = (from: string, to: string) => {
  const m = Math.round((Date.parse(to) - Date.parse(from)) / 60_000);
  return m < 1 ? text(TEXT).request.underMinute : `${m} min`;
};

/** Where each video's credits went: its requests, and in each one the
 * Director's work by stage and the media it generated. */
function ByVideo() {
  const t = useText(TEXT).videos;
  const [videos, setVideos] = useState<VideoSpend[] | null>(null),
    [open, setOpen] = useState(""),
    [error, setError] = useState("");
  useEffect(() => {
    void api<{ videos: VideoSpend[] }>("/credits/videos")
      .then(({ videos }) => {
        setVideos(videos);
        setOpen(videos[0]?.projectId || "");
      })
      .catch((e) => setError(e.message));
  }, []);
  const top = Math.max(1, ...(videos || []).map((v) => v.total));
  return (
    <section className="card by-video" aria-labelledby="by-video-title">
      <div className="bv-h">
        <h2 className="h2" id="by-video-title">
          {t.title}
        </h2>
        {videos && videos.length > 1 && (
          <span className="mono">
            {t.average(
              credits(videos.reduce((n, v) => n + v.total, 0) / videos.length),
            )}
          </span>
        )}
      </div>
      {error && <p className="form-error">{error}</p>}
      {videos && !videos.length && <p className="muted">{t.none}</p>}
      <ul className="bv-list">
        {(videos || []).map((v) => {
          const expanded = open === v.projectId;
          return (
            <li key={v.projectId} className={expanded ? "open" : ""}>
              <button
                className="bv-row"
                aria-expanded={expanded}
                onClick={() => setOpen(expanded ? "" : v.projectId)}
              >
                <span className="bv-thumb">
                  {v.poster ? <img src={v.poster} alt="" /> : <Mark />}
                </span>
                <span className="bv-t">
                  <b>{v.title}</b>
                  <small>
                    {v.deleted ? t.deleted : t.requests(v.requests.length)} ·{" "}
                    {relativeTime(v.lastAt)}
                  </small>
                </span>
                <span className="bar" aria-hidden="true">
                  <i style={{ width: `${(v.total / top) * 100}%` }} />
                </span>
                <span className="bv-n mono">{tenths(v.total)}</span>
                <ChevronDown size={16} className="bv-chev" />
              </button>
              {expanded && (
                <ol className="bv-reqs">
                  {v.requests.map((r) => (
                    <Request key={r.id} request={r} />
                  ))}
                  {!v.deleted && (
                    <li className="bv-open">
                      <Link to={`/project/${v.projectId}`}>
                        {t.open} <ArrowRight size={14} />
                      </Link>
                    </li>
                  )}
                </ol>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

// Bills read in tenths, so lines line up.
const tenths = (n: number) =>
  new Intl.NumberFormat(locale(), {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(n);

function Request({ request: r }: { request: VideoSpend["requests"][number] }) {
  const copy = useText(TEXT),
    t = copy.request;
  const groups = [
    {
      title: t.director,
      lines: r.direction.map((d) => ({
        key: d.stage || "direction",
        Icon: null,
        label: d.stage ? text(STAGES)[d.stage].noun : copy.categories.inference,
        note: "",
        amount: d.amount,
      })),
    },
    {
      title: t.generated,
      lines: r.media.map((m) => ({
        key: m.category,
        Icon: icons[m.category],
        label: t.media[m.category][m.count === 1 ? 0 : 1],
        note: m.count > 1 ? `×${m.count}` : "",
        amount: m.amount,
      })),
    },
  ]
    .map((g) => ({ ...g, lines: g.lines.sort((a, b) => b.amount - a.amount) }))
    .filter((g) => g.lines.length);
  const top = Math.max(
    1,
    ...groups.flatMap((g) => g.lines.map((l) => l.amount)),
  );
  return (
    <li className="rq">
      <div className="rq-head">
        <span className="rq-text">{r.text ? `“${r.text}”` : t.yourClaude}</span>
        <span className="rq-n mono">{tenths(r.total)}</span>
      </div>
      <p className="rq-meta">
        {relativeTime(r.at)}
        {r.endedAt && t.worked(lasted(r.at, r.endedAt))}
        {r.reserved > 0 && t.onHold(tenths(r.reserved))}
        {r.made.length > 0 && (
          <>
            {t.made}
            <b>{r.made.map((t) => `“${t}”`).join(", ")}</b>
          </>
        )}
      </p>
      {groups.map((g) => (
        <div className="rq-group" key={g.title}>
          <h3>{g.title}</h3>
          <ul className="rq-lines">
            {g.lines.map(({ key, Icon, label, note, amount }) => (
              <li key={key}>
                <span className="rq-l">
                  {Icon && <Icon size={14} />} {label}
                  {note && <small>{note}</small>}
                </span>
                <span className="bar" aria-hidden="true">
                  <i style={{ width: `${(amount / top) * 100}%` }} />
                </span>
                <span className="mono">{tenths(amount)}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </li>
  );
}
